/**
 * Admin Panel Access Management — permission model.
 *
 * Single source of truth for the 21 PRD sections, their backend module keys and
 * the actions each one supports. Everything else (nav, route guards, the Role
 * Management grid, per-screen buttons) reads from here so the model can never
 * drift between screens.
 *
 * Rules from the API contract:
 *   - `view` and `edit` exist on every section. `edit` covers create + update;
 *     there is no separate "create" permission.
 *   - `delete` exists ONLY on the five Content sub-modules. No Delete toggle is
 *     rendered anywhere else — the backend has no delete permission defined for
 *     them, so the check would always be false.
 *   - A missing module key or action key means `false`.
 *   - Super Admin bypasses this map entirely (see `isSuperAdminRole`).
 */

export const SUPER_ADMIN_ROLE = 'super_admin';

/**
 * ─── Genres / Tags key binding ────────────────────────────────────────────────
 * The UI labels for the lookup screens were swapped at some point without
 * renaming the routes, so today:
 *
 *   UI "Categories"  → route /genres → Categories API (/admin/v1/category/*)
 *   UI "Genres"      → route /tags   → Tags API       (/admin/v1/tag/*)
 *   UI "Tags"        → route /types  → Types API      (/admin/v1/type/*)
 *
 * The integration doc maps by UI label (Genres → `types`, Tags → `tags`). We map
 * by the backend resource each screen actually calls, so that what the nav hides
 * always matches what the server will actually reject. If the backend turns out
 * to guard by UI label instead, flip this one flag and nothing else changes.
 */
export const MAP_LOOKUP_KEYS_BY_UI_LABEL = false;

const GENRES_SCREEN_KEY = MAP_LOOKUP_KEYS_BY_UI_LABEL ? 'types' : 'tags';
const TAGS_SCREEN_KEY = MAP_LOOKUP_KEYS_BY_UI_LABEL ? 'tags' : 'types';

export const PERM = {
    DASHBOARD: 'dashboard',
    ANALYTICS: 'analytics',
    USERS: 'users',
    SERIES: 'series',
    CATEGORIES: 'categories',
    GENRES: GENRES_SCREEN_KEY,
    TAGS: TAGS_SCREEN_KEY,
    BANNER_MANAGEMENT: 'banner_management',
    TRAILER_RANKING: 'trailer_ranking',
    PACKAGES: 'packages',
    PAYMENT_METHOD: 'payment_method',
    LANGUAGES: 'languages',
    REWARD: 'reward',
    SETTINGS: 'settings',
    ADS_SETTING: 'ads_setting',
    SEND_NOTIFICATION: 'send_notification',
    TICKETS: 'tickets',
    ROLE_MANAGEMENT: 'role_management',
    ADMIN_USERS: 'admin_users',
    ABOUT_US: 'about_us',
    PRIVACY_POLICY: 'privacy_policy',
    TERMS_AND_CONDITIONS: 'terms_and_conditions',
    // Edit also gates changing a series' audience (CI-958).
    USER_SEGMENTS: 'user_segments',
};

/** The only modules on which a `delete` permission exists. */
export const DELETABLE_MODULES = Object.freeze([
    PERM.SERIES,
    PERM.CATEGORIES,
    PERM.GENRES,
    PERM.TAGS,
    PERM.BANNER_MANAGEMENT,
    PERM.USER_SEGMENTS,
]);

export const canModuleBeDeleted = (module) => DELETABLE_MODULES.includes(module);

const VE = ['view', 'edit'];
const VED = ['view', 'edit', 'delete'];

/**
 * The permission grid, grouped exactly as the PRD lists the 21 sections.
 * `key: null` marks a section that has no dedicated backend module yet.
 */
export const PERMISSION_GROUPS = [
    {
        group: 'Dashboard',
        sections: [{ label: 'Dashboard', key: PERM.DASHBOARD, actions: ['view'] }],
    },
    {
        group: 'Analytics',
        sections: [{ label: 'Analytics', key: PERM.ANALYTICS, actions: VE }],
    },
    {
        group: 'Users',
        sections: [{ label: 'Users', key: PERM.USERS, actions: VE }],
    },
    {
        group: 'Content',
        sections: [
            { label: 'Series', key: PERM.SERIES, actions: VED },
            { label: 'Categories', key: PERM.CATEGORIES, actions: VED },
            { label: 'Genres', key: PERM.GENRES, actions: VED },
            { label: 'Tags', key: PERM.TAGS, actions: VED },
            { label: 'Banner Management', key: PERM.BANNER_MANAGEMENT, actions: VED },
            { label: 'Trailer Ranking', key: PERM.TRAILER_RANKING, actions: VE },
        ],
    },
    {
        group: 'Packages',
        sections: [{ label: 'Packages', key: PERM.PACKAGES, actions: VE }],
    },
    {
        group: 'General',
        sections: [
            { label: 'Payment Method', key: PERM.PAYMENT_METHOD, actions: VE },
            { label: 'Languages', key: PERM.LANGUAGES, actions: VE },
            { label: 'Reward', key: PERM.REWARD, actions: VE },
            { label: 'Settings', key: PERM.SETTINGS, actions: VE },
            { label: 'Ad Settings', key: PERM.ADS_SETTING, actions: VE },
            { label: 'Send Notifications', key: PERM.SEND_NOTIFICATION, actions: VE },
            { label: 'Tickets', key: PERM.TICKETS, actions: VE },
        ],
    },
    {
        group: 'Admin',
        sections: [
            // Role writes are Super-Admin-only, so only View is delegable here.
            { label: 'Role Management', key: PERM.ROLE_MANAGEMENT, actions: ['view'] },
            { label: 'Team Members', key: PERM.ADMIN_USERS, actions: VE },
            { label: 'About Us', key: PERM.ABOUT_US, actions: VE },
            { label: 'Privacy Policy', key: PERM.PRIVACY_POLICY, actions: VE },
            { label: 'Terms & Conditions', key: PERM.TERMS_AND_CONDITIONS, actions: VE },
            { label: 'User Segments', key: PERM.USER_SEGMENTS, actions: VED },
        ],
    },
];

/** Flat list of every configurable section (Reward included, key may be null). */
export const ALL_SECTIONS = PERMISSION_GROUPS.flatMap((g) =>
    g.sections.map((s) => ({ ...s, group: g.group })),
);

/** Flat list of the sections that map to a real backend module key. */
export const TOGGLEABLE_SECTIONS = ALL_SECTIONS.filter((s) => !!s.key);

/** Total number of individual toggles in the grid — used for the progress bar. */
export const TOTAL_TOGGLE_COUNT = TOGGLEABLE_SECTIONS.reduce(
    (sum, s) => sum + s.actions.length,
    0,
);

/** A permissions map with every action explicitly set to false. */
export const buildEmptyPermissions = () => {
    const perms = {};
    TOGGLEABLE_SECTIONS.forEach((section) => {
        perms[section.key] = {};
        section.actions.forEach((action) => {
            perms[section.key][action] = false;
        });
    });
    return perms;
};

/** A permissions map with every action set to true (Super Admin display only). */
export const buildFullPermissions = () => {
    const perms = {};
    TOGGLEABLE_SECTIONS.forEach((section) => {
        perms[section.key] = {};
        section.actions.forEach((action) => {
            perms[section.key][action] = true;
        });
    });
    return perms;
};

/**
 * The role can arrive as a string (legacy) or as `{ id, name, ... }` (current
 * contract), and is `null` when the admin has no role — treated as zero access.
 */
export const getRoleName = (role) => {
    if (!role) return null;
    if (typeof role === 'string') return role;
    return role.name || null;
};

export const isSuperAdminRole = (role) => getRoleName(role) === SUPER_ADMIN_ROLE;

/** Counts granted toggles for a role, ignoring anything outside the 21 sections. */
export const countGrantedPermissions = (permissions) => {
    let granted = 0;
    TOGGLEABLE_SECTIONS.forEach((section) => {
        section.actions.forEach((action) => {
            if (permissions?.[section.key]?.[action]) granted += 1;
        });
    });
    return { granted, total: TOTAL_TOGGLE_COUNT };
};
